import React, { useState } from 'react';

import { TestExecutionStep, TestStepsView } from '@qality/common-model';
import { ViewService } from '@qality/common-service';

import TestExecutionDetailsTableTitles from './TestExecutionDetailsTableTitles';
import TestExecutionTableHeader from './TestExecutionTableHeader';
import TestExecutionTableRow from './TestExecutionTableRow';

type Props = {
  testSteps: Array<TestExecutionStep>;
  testExecutionId: number;
  onUpdateTestStep: (
    testStepIndex: number,
    testStep: TestExecutionStep
  ) => Promise<void>;
  onBulkUpdateTestStep: (testSteps: Array<TestExecutionStep>) => Promise<void>;
  onUpdateTestStepSelection: (
    testStepIndex: number,
    testStep: TestExecutionStep
  ) => void;
  testCaseIssueKey: string;
  onAddBug: () => Promise<void>;
};

function TestExecutionDetails({
  testSteps,
  testExecutionId,
  onUpdateTestStep,
  onBulkUpdateTestStep,
  onUpdateTestStepSelection,
  testCaseIssueKey,
  onAddBug,
}: Props): JSX.Element {
  const [viewType, setViewType] = useState<TestStepsView>(
    ViewService.fetchExecutionViewType(testExecutionId)
  );
  const [numberOfSelectedItems, setNumberOfSelectedItems] = useState(0);
  const [selectAll, setSelectAll] = useState(false);

  return (
    <>
      <div className='test-execution-details'>
        <TestExecutionDetailsTableTitles
          testSteps={testSteps}
          onBulkUpdateTestStep={onBulkUpdateTestStep}
          numberOfSelectedItems={numberOfSelectedItems}
          view={viewType}
          onChangeView={changeView}
        />
        <div>
          <TestExecutionTableHeader
            view={viewType}
            selectAll={selectAll}
            onSelectAllChange={updateSelectionInSteps}
          />
          {mapTestStepsToComponents()}
        </div>
      </div>
    </>
  );

  function mapTestStepsToComponents() {
    return testSteps.map((testStep, testStepIndex) => {
      return (
        <TestExecutionTableRow
          key={testStep.id}
          testStep={testStep}
          view={viewType}
          testCaseIssueKey={testCaseIssueKey}
          testStepIndex={testStepIndex}
          onUpdateTestStep={onUpdateTestStep}
          onAddBug={onAddBug}
          onUpdateTestStepSelection={(
            event: React.ChangeEvent<HTMLInputElement>
          ) => {
            testStep.isSelected = event.target.checked;
            onUpdateTestStepSelection(testStepIndex, testStep);
            setNumberOfSelectedItems(countSelectedItems(testSteps));

            if (testSteps.every((ts) => ts.isSelected)) {
              setSelectAll(true);
            } else {
              setSelectAll(false);
            }
          }}
        />
      );
    });
  }

  function changeView(newViewType: TestStepsView) {
    ViewService.saveExecutionViewType(testExecutionId, newViewType);
    setViewType(newViewType);
  }

  function updateSelectionInSteps(event: React.ChangeEvent<HTMLInputElement>) {
    setSelectAll(event.target.checked);
    testSteps.forEach((testStep, testStepIndex) => {
      if (testStep.isSelected !== event.target.checked) {
        testStep.isSelected = event.target.checked;
        onUpdateTestStepSelection(testStepIndex, testStep);
      }
    });
    setNumberOfSelectedItems(countSelectedItems(testSteps));
  }
}

function countSelectedItems(testSteps: Array<TestExecutionStep>): number {
  return testSteps.filter((step: TestExecutionStep) => step.isSelected).length;
}

export default TestExecutionDetails;
